/**
 * Ask a records question.
 *
 * Open to every manager as of 2026-08-18. It shipped commissioner-only because it cost money per
 * question and nobody had seen whether the answers were any good; both are now settled. The
 * common misses never reach the model, the canned questions run with none at all, and a
 * misunderstood question produces a visibly wrong QUERY rather than a plausible wrong number.
 *
 * THE MODEL WRITES A QUERY. IT DOES NOT WRITE AN ANSWER. What comes back over the wire is a JSON
 * query object, which is validated, shown on screen, and then run against the archive HERE in the
 * browser by `domain/recordsQuery.ts`. Every number under the question was computed from the same
 * rows the rest of the app uses.
 *
 * That is why the query is printed above the result rather than hidden behind a disclosure. It is
 * not debug output — it is the working. A record that gets quoted back at somebody in an argument
 * needs to be arguable with, and "the computer said so" is not arguable with. If the model
 * misreads the question, what you see is a query that plainly asks the wrong thing, which is a
 * failure you can spot in a second and correct by rewording.
 */

import { useState } from 'react';
import {
  EXAMPLE, QueryError, TREND, describeQuery, parseQuery, runQuery, schemaPrompt,
  type GameLink, type Query, type QueryResult, type ResultRow,
} from '../domain/recordsQuery.js';
import {
  REFUSALS, classify, parseRefusal, refusalCopy, refusalPrompt, type RefusalCopy,
} from '../domain/askRefusals.js';
import { idToken } from '../store/session.js';

/**
 * Questions that run with no model at all.
 *
 * Not decoration. They are how you tell whether a disappointing answer is the model misreading
 * you or the engine getting it wrong, and they keep the whole screen usable in the standalone
 * build, which has no server behind it.
 */
const CANNED: { q: string; query: Query }[] = [
  EXAMPLE,
  {
    question: 'Most points ever left on a bench',
    query: {
      from: 'sides', groupBy: 'game', measure: { fn: 'max', field: 'bench' },
      order: 'desc', limit: 10,
    },
  },
  {
    question: 'Who has won the most games?',
    query: {
      from: 'sides', where: [{ field: 'won', op: '=', value: true }],
      groupBy: 'gm', measure: { fn: 'count' }, order: 'desc', limit: 10,
      reading: 'Counting outright wins per manager, most first.',
    },
  },
  /*
   * The one that used to be unaskable, kept here as the worked example of a RATE.
   *
   * "Who's the worst manager?" was the question that showed the language up: it has no answer in
   * counts, because the man with most losses is just the man who has been here longest. It needs a
   * percentage and a floor under it, and having both, it is now the most-asked shape on the screen.
   */
  {
    question: 'Worst manager, by win percentage',
    query: {
      from: 'sides', groupBy: 'gm', measure: { fn: 'pct', field: 'won' },
      order: 'asc', limit: 10, minRows: 20,
      reading: 'The share of weeks each manager won, fewest first, ignoring the barely-there.',
    },
  },
  /*
   * The trend, canned, because it is the shape the model most reliably gets wrong.
   *
   * Tim asked for the average QB score over ten years and got a table of PLAYERS with Jake
   * Browning third on one good week. Two faults, both fixed: a grouped average now takes a floor,
   * and a question about change over time sorts by season rather than by size. Having it here
   * means the shape is one tap away whether or not the model reaches for it.
   */
  TREND,
  {
    question: 'Highest single week ever',
    query: {
      from: 'sides', groupBy: 'game', measure: { fn: 'max', field: 'points' },
      order: 'desc', limit: 10,
    },
  },
].map((x) => ('question' in x ? { q: x.question, query: x.query } : x)) as { q: string; query: Query }[];

/**
 * The four-point sparkle that has come to mean "a model did this".
 *
 * Drawn to the same rules as the nav icons — 24-unit grid, `currentColor`, no fill — so it sits
 * beside a heading without announcing itself. It marks the ONE feature in this app that asks a
 * model anything; everything else here is arithmetic over the league's own record.
 */
function SparkIcon() {
  return (
    <svg className="sparkicon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"
      aria-hidden="true">
      <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z" />
      <path d="M18.5 15.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z" opacity=".55" />
    </svg>
  );
}

/** A query that measured everything at once rather than grouping. */
const ungrouped = (r: QueryResult) => r.rows.length === 1 && r.rows[0]?.key === 'all';

/**
 * The rows, linked where a row is about one game.
 *
 * A row without a link is still a row — grouping by manager or season produces perfectly good
 * answers that no single box score backs — so the link is an affordance on the ones that have it
 * rather than a shape the whole table has to take.
 */
function Rows(
  { rows, onOpenGame, ranked, more, unit }:
  {
    rows: ResultRow[]; onOpenGame?: (g: GameLink) => void; ranked?: boolean; more: number;
    /** `%` on a rate. A column of bare `21.43` next to a column of counts reads as points. */
    unit?: string;
  },
) {
  if (!rows.length) return null;
  return (
    <>
      <table className="askrows">
        <thead>
          <tr>
            {ranked && <th>#</th>}
            <th>What</th>
            <th className="num">Value</th>
            {ranked && <th className="num">Rows</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key}>
              {ranked && <td className="rk">{i + 1}</td>}
              <td>
                {r.ref && onOpenGame ? (
                  <button type="button" className="linkbtn asklink"
                    title="Open that box score"
                    onClick={() => onOpenGame(r.ref!)}>
                    {r.label}
                  </button>
                ) : r.label}
              </td>
              <td className="num strong">{r.value.toLocaleString('en-US')}{unit}</td>
              {ranked && <td className="num mut">{r.rows}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {more > 0 && (
        <p className="note">
          Showing the first {rows.length}. {more.toLocaleString('en-US')} more match.
        </p>
      )}
    </>
  );
}

type State =
  | { at: 'idle' }
  | { at: 'thinking' }
  | { at: 'done'; query: Query; result: QueryResult; asked: string }
  /**
   * A question that did not run.
   *
   * `copy` is what makes this a screen rather than a wall. A box that takes plain English gets
   * asked plain English, and most of English is not a records question — whose team name is worst,
   * who wins this year, why somebody benched somebody. Each kind of miss gets its own sentence
   * from `askRefusals` and two questions that DO run, offered as buttons. `raw` is kept on every
   * failure because the shape that came back is the only thing that says which end went wrong.
   */
  | { at: 'failed'; copy: RefusalCopy; raw?: string };

export function AskRecords({ onOpenGame }: { onOpenGame?: (g: GameLink) => void }) {
  const [question, setQuestion] = useState('');
  const [state, setState] = useState<State>({ at: 'idle' });

  /** Run a query we already have — the canned ones, and the model's once it has been parsed. */
  function run(query: Query, asked: string) {
    try {
      setState({ at: 'done', query, result: runQuery(query), asked });
    } catch (e) {
      setState({
        at: 'failed',
        copy: {
          ...REFUSALS.unclear, tone: 'fault',
          line: e instanceof Error ? e.message : REFUSALS.unclear.line,
        },
      });
    }
  }

  const fail = (copy: RefusalCopy, raw?: string) => setState({ at: 'failed', copy, raw });

  /**
   * Take a suggestion.
   *
   * Every suggestion offered is the text of a canned question, so this runs the stored query and
   * answers instantly rather than paying for a second model call to rediscover a query we already
   * hold. Anything else typed goes the long way.
   */
  function take(text: string) {
    const canned = CANNED.find((c) => c.q === text);
    setQuestion(text);
    if (canned) run(canned.query, text);
    else void ask(text);
  }

  async function ask(text?: string) {
    const q = (text ?? question).trim();
    if (!q) return;
    if (text) setQuestion(text);

    /*
     * THE OBVIOUS MISSES NEVER LEAVE THE BROWSER.
     *
     * Taste, prophecy, motive and small talk are all recognisable from the question alone, and
     * sending them to a paid model to be told the same thing costs money and two seconds to
     * arrive at a worse-worded no. It also means the standalone build — which has no server at
     * all — answers them properly instead of failing to connect.
     */
    const obvious = classify(q);
    if (obvious) {
      fail(REFUSALS[obvious]);
      return;
    }

    setState({ at: 'thinking' });
    try {
      const token = await idToken();
      const res = await fetch('/api/ask', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        // The schema travels with the question so the model's picture of the data and the code
        // that runs the query can never be a deploy apart.
        body: JSON.stringify({ question: q, schema: `${schemaPrompt()}\n\n${refusalPrompt()}` }),
      });
      const body = await res.json() as { raw?: string; error?: string };
      if (!res.ok || body.error) {
        fail({
          head: 'That did not get as far as the archive.',
          line: body.error ?? `The server returned ${res.status}.`,
          suggest: REFUSALS.unclear.suggest,
          tone: 'fault',
        });
        return;
      }

      let parsed: unknown;
      try {
        // A fence survives `responseMimeType: application/json` more often than it should.
        parsed = JSON.parse((body.raw ?? '').replace(/^```(?:json)?|```$/g, '').trim());
      } catch {
        fail({ ...REFUSALS.unclear, tone: 'fault', line: 'The model did not return a query.' },
          body.raw);
        return;
      }

      /*
       * The model is allowed to say it cannot express the question, and that is a BETTER answer
       * than a query that quietly means something else. It is only useful if it is recognised,
       * though — this used to look for one exact shape, so a refusal phrased any other way fell
       * through to the query parser and came back as "'undefined' is not something to ask about",
       * which is an error message about the model's grammar rather than about your question.
       *
       * The model names the KIND; the words come from here. A refusal that sounds like the app is
       * worth more than one that sounds like an assistant apologising, and it says the same thing
       * every time, which nothing generated ever quite does.
       */
      const refused = parseRefusal(parsed);
      if (refused) {
        fail(refusalCopy(refused), body.raw);
        return;
      }

      try {
        run(parseQuery(parsed), q);
      } catch (e) {
        fail({
          ...REFUSALS.unclear, tone: 'fault',
          line: e instanceof QueryError ? e.message : 'That query would not run.',
        }, body.raw);
      }
    } catch {
      fail({
        head: 'Could not reach the model.',
        line: 'The examples below still run — they need no server at all.',
        suggest: REFUSALS.unclear.suggest,
        tone: 'fault',
      });
    }
  }

  return (
    <section className="card askcard">
      <div className="striptop">
        <h3 className="striphead">Ask the Record<SparkIcon /></h3>
        <span className="chip warning">prototype</span>
      </div>

      <p className="note">
        Ask in plain words. The model writes a query; the app runs it against the archive and shows
        you both, so every number here can be checked against the query that produced it.
      </p>

      <div className="askrow">
        <input
          className="search"
          type="search"
          value={question}
          placeholder="Most 0.0 scores on a winning starting lineup?"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void ask(); }}
        />
        <button type="button" className="btn primary" disabled={!question.trim() || state.at === 'thinking'}
          onClick={() => void ask()}>
          {state.at === 'thinking' ? 'Thinking…' : 'Ask'}
        </button>
      </div>

      <div className="askexamples">
        <span className="mut">Runs with no model:</span>
        {CANNED.map((c) => (
          <button type="button" className="chip" key={c.q}
            onClick={() => { setQuestion(c.q); run(c.query, c.q); }}>
            {c.q}
          </button>
        ))}
      </div>

      {state.at === 'failed' && (
        <div className={`askfail ${state.copy.tone ?? 'fault'}`}>
          <b>{state.copy.head}</b>
          <p>{state.copy.line}</p>
          {/*
            The way out. Nobody asking a question has any way to know where the archive's edge
            falls, and two questions that DO run teach the shape of the thing far faster than any
            amount of explaining would.
          */}
          <div className="askexamples">
            <span className="mut">Try instead:</span>
            {state.copy.suggest.map((s) => (
              <button type="button" className="chip" key={s} onClick={() => take(s)}>{s}</button>
            ))}
          </div>
          {state.raw && (
            <details className="askraw">
              <summary className="mut">What came back</summary>
              <pre className="askquery">{state.raw}</pre>
            </details>
          )}
        </div>
      )}

      {state.at === 'done' && (
        <div className="askresult">
          <div className="askhead">
            {/*
              The row tally used to sit here. It says the same thing the first context line now
              says, in worse words, and twice is once too many.
            */}
            <b>{state.asked}</b>
          </div>

          {/*
            The working, in a sentence. This used to print the query object itself, which was a
            wall of JSON above every answer — Tim's call to drop it, and he is right: what mattered
            was being able to see what your question was TURNED INTO and catch the model misreading
            you, and one line of English does that better than braces do.
          */}
          {/*
            THE ONE SENTENCE THE MODEL IS ALLOWED TO WRITE.
            It says what it thought you were asking, and it cannot contain a digit — the parser
            throws away any reading that does — so it can restate a question but never assert a
            record. Everything numeric on this screen was computed downstairs.
          */}
          {state.query.reading && <p className="askreading">{state.query.reading}</p>}

          <p className="askread">{describeQuery(state.query)}</p>

          {state.result.rows.length === 0 ? (
            <p className="note">Nothing in the archive matches that.</p>
          ) : ungrouped(state.result) ? (
            /*
             * A COUNT ON ITS OWN IS THE LEAST USEFUL TRUE THING AN ARCHIVE CAN SAY.
             *
             * "21" is an answer nobody can check, argue with, or enjoy. The number gets its size,
             * and under it go the twenty-one dated matchups it is made of — each one a tap from
             * the box score that proves it.
             */
            <>
              <div className="askbig">
                <b>{state.result.rows[0]!.value.toLocaleString('en-US')}</b>
                <span className="mut">
                  {state.result.matched === state.result.rows[0]!.value
                    ? 'matching rows'
                    : `from ${state.result.matched.toLocaleString('en-US')} matching rows`}
                </span>
              </div>
              <Rows rows={state.result.examples} onOpenGame={onOpenGame}
                more={state.result.matched - state.result.examples.length} />
            </>
          ) : (
            /*
              A trend gets no rank column. The numbers down the left of a table read as places,
              and "2016 is 1st" is not a thing the table is saying.
            */
            <Rows rows={state.result.rows} onOpenGame={onOpenGame} more={0}
              ranked={state.query.sort !== 'key'}
              unit={state.query.measure.fn === 'pct' ? '%' : ''} />
          )}

          {/*
            WHAT THE NUMBER IS STANDING NEXT TO — how much of the record it saw, where the rest of
            the field sits, what a rate floor left out. Arithmetic over the same rows, so it is as
            checkable as the table above it, and it is the difference between a figure somebody
            quotes and a figure somebody can defend.
          */}
          {state.result.context.length > 0 && (
            <ul className="askcontext">
              {state.result.context.map((line) => <li key={line}>{line}</li>)}
            </ul>
          )}

          <p className="note">
            Built from the box-score archive, so a game with no box score is not in the count.
          </p>
        </div>
      )}
    </section>
  );
}
